import { Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { NotificationService } from '@app/core/services/notification.service'
import { Observable, of, throwError } from 'rxjs'
import { Router } from '@angular/router'
import { applyTransaction } from '@datorama/akita'
import type { FormState } from '@app/shared/models/form.model'
import type { EntityUpdateRequest } from '@app/shared/models/entity-update.model'
import { SharedAhuService } from '@app/shared/services/ahu/shared-ahu.service'
import { UserService } from '@app/user/state/user.service'
import { EventsService } from '@app/shared/services/events/events.service'
import { saveAs } from 'file-saver'
import { TranslateService } from '@ngx-translate/core'
import { AhuStore } from './ahu.store'
import {
  AhuDto,
  AhuResultDto,
  AhuUpdateDto,
  ConvertAhuDtoToDomainModel,
  ConvertAhuResultDtoToDomainModel,
  ConvertAhuUpdateDtoToDomainModel,
  QuickCalcAhuDto,
} from './ahu.dto.model'
import { Ahu, AhuResults, ViewOption } from './ahu.model'
import { AhuQuery } from './ahu.query'

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class AhuService {
  constructor(
    private router: Router,
    private ahuStore: AhuStore,
    private http: HttpClient,
    private notificationService: NotificationService,
    private sharedAhuService: SharedAhuService,
    private userService: UserService,
    private ahuQuery: AhuQuery,
    private eventsService: EventsService,
    private translateService: TranslateService,
  ) {
    this.userService.settingsChanged$
      .pipe(
        switchMap(() => {
          const active = this.ahuQuery.getActive()

          if (!active) {
            return of(true)
          }

          // If the current AHU has results loaded, set the flag to reload them after cache is cleared
          const reloadResults = !!active.Results

          this.ahuStore.remove()

          if (active) {
            this.getAhu(active.Id, active.ProjectId, reloadResults)
          }

          return of(true)
        }),
        untilDestroyed(this),
      )
      .subscribe()

    this.eventsService.projectChangedEvent$
      .pipe(
        tap({
          next: () => {
            this.ahuStore.update(null, {
              ShouldRefresh: true,
              Results: null,
            })
          },
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  getAnonymousAhu(countryId: string, unitCode?: string): void {
    this.internalGetAnonymousAhu(countryId, unitCode)
      .pipe(
        tap({
          next: ahu => {
            this.ahuStore.setActive(ahu.Id)
          },
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  getAhu(id: string, projectId: string, loadResults: boolean = false): void {
    this.internalGetAhu(id, projectId)
      .pipe(
        tap(() => {
          this.ahuStore.setActive(id)
          if (loadResults) {
            this.getResults(id).subscribe()
          }
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  calculateAhu(id: string, skipEvents: boolean = false, skipNotifications: boolean = false): Observable<AhuResults> {
    return this.http.post<AhuResultDto>(`./Api/ahu/${id}/results`, null).pipe(
      map(dto => {
        return ConvertAhuResultDtoToDomainModel(dto)
      }),
      tap(results => {
        applyTransaction(() => {
          // Note that we do an update, not an upsert: if we do an upsert, we end up with a record of an AHU which doesn't
          // actually contain the AHU data - it will only contain the AHU results! This will cause the AHU page to break.
          this.ahuStore.update(id, { Results: results })

          this.setDefaultRenderImageIfNotSet(id)
          this.getSystemDrawingImage(id)
          this.getHeatingDurationDiagramImage(id)
          this.getVentilationDurationDiagramImage(id)
          this.getEnergyCoverageImage(id)
        })
        if (!skipEvents) {
          this.eventsService.ahuChanged(id)
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (!skipNotifications) {
          this.notificationService.error('UI.Error.GetResults', { error: error?.statusText })
        }
        return throwError(() => error)
      }),
      untilDestroyed(this),
    )
  }

  getResults(id: string): Observable<AhuResults> {
    return this.calculateAhu(id)
  }

  private getAhuViewImageUrl(id: string, width: number, height: number, view: ViewOption): string {
    return `./Api/ahu/${id}/renderUrl/${view}/${width}/${height}?decache=${Number(new Date())}`
  }

  private setDefaultRenderImageIfNotSet(id: string): void {
    const width = 700
    const height = 700

    this.ahuStore.update(id, ahu => {
      if (!ahu.RenderImage) {
        return {
          ...ahu,
          RenderImage: {
            View: ViewOption.TOPLEFT,
            Source: this.getAhuViewImageUrl(id, width, height, ViewOption.TOPLEFT),
          },
        }
      }
      return ahu
    })
  }

  updateRenderImage(id: string, view: ViewOption | 'unit' | 'connections'): void {
    const width = 700
    const height = 700

    this.ahuStore.update(id, ahu => {
      // Pick the correct URL for the chosen image
      let url = this.getAhuViewImageUrl(id, width, height, view as ViewOption)
      if (view === 'unit') {
        url = ahu.UnitImageURL
      }
      if (view === 'connections') {
        url = ahu.UnitConnectionURL
      }

      // If the URL doesn't change - do not update the ahu
      // This is to prevent unnecessarily triggering a state update when nothing actually changes
      if (ahu.RenderImage && ahu.RenderImage.Source === url) {
        return ahu
      }

      return {
        ...ahu,
        RenderImage: {
          View: view,
          Source: url,
        },
      }
    })
  }

  getSystemDrawingImage(id: string): void {
    this.ahuStore.update(id, {
      SystemDrawingImage: `./Api/ahu/${id}/systemdrawing?decache=${Number(new Date())}`,
    })
  }

  getEnergyCoverageImage(id: string, country: string = null): void {
    const width = 700
    const height = 350

    const countryQuery = country ? `/country/${country}` : ''

    this.ahuStore.update(id, {
      EnergyCoverageImage: `./Api/ahu/${id}/Diagram/energycoverage/${width}/${height}${countryQuery}?decache=${Number(new Date())}`,
    })
  }

  getVentilationDurationDiagramImage(id: string, country: string = null): void {
    const width = 700
    const height = 350

    const countryQuery = country ? `/country/${country}` : ''

    this.ahuStore.update(id, {
      VentilationDurationDiagramImage: `./Api/ahu/${id}/Diagram/ventilationduration/${width}/${height}${countryQuery}?decache=${Number(
        new Date(),
      )}`,
    })
  }

  getHeatingDurationDiagramImage(id: string, country: string = null): void {
    const width = 700
    const height = 350

    const countryQuery = country ? `/country/${country}` : ''

    this.ahuStore.update(id, {
      HeatingDurationDiagramImage: `./Api/ahu/${id}/Diagram/heatingduration/${width}/${height}${countryQuery}?decache=${Number(
        new Date(),
      )}`,
    })
  }

  updateAnonymous(id: string, properties: EntityUpdateRequest, countryId: string): Observable<AhuUpdateDto> {
    return this.http.patch<AhuUpdateDto>(`./Api/ahu/${id}/country/${countryId}`, properties).pipe(
      tap({
        next: ahuUpdateResult => {
          applyTransaction(() => {
            this.ahuStore.update(id, oldState => {
              return {
                ...oldState,
                ...ConvertAhuUpdateDtoToDomainModel(ahuUpdateResult),
              }
            })
            this.updateFormState({ isModified: true })
          })
        },
      }),
      catchError(error => {
        this.notificationService.error('UI.Error.AhuUpdated')
        return throwError(error)
      }),
    )
  }

  update(id: string, properties: EntityUpdateRequest): Observable<AhuUpdateDto> {
    return this.http.patch<AhuUpdateDto>(`./Api/ahu/${id}`, properties).pipe(
      tap({
        next: ahuUpdateResult => {
          applyTransaction(() => {
            this.ahuStore.update(id, oldState => {
              return {
                ...oldState,
                ...ConvertAhuUpdateDtoToDomainModel(ahuUpdateResult),
              }
            })
            this.updateFormState({ isModified: true })
          })
        },
      }),
      catchError(error => {
        this.notificationService.error('UI.Error.AhuUpdated')
        return throwError(error)
      }),
    )
  }

  updateFormState(newState: Partial<FormState>) {
    this.ahuStore.update(state => ({ ui: { ...state.ui, formState: { ...state.ui.formState, ...newState } } }))
  }

  discard(id: string, omitSuccessMessage: boolean = false): Observable<Ahu> {
    return this.http.post<HttpResponse<null>>(`./Api/ahu/${id}/reset`, null).pipe(
      switchMap(() => {
        return this.internalGetAhu(id, this.ahuQuery.getActive().ProjectId)
      }),
      tap(() => {
        this.updateFormState({ isValid: true, isModified: false })
        if (!omitSuccessMessage) {
          this.notificationService.success('UI.Success.DataDiscard')
        }
      }),
      catchError(error => {
        this.notificationService.error('UI.Error.DataDiscard')
        return throwError(error)
      }),
    )
  }

  save(id: string, projectId: string, omitSuccessMessage: boolean = false): Observable<boolean> {
    return this.http.post<HttpResponse<null>>(`./Api/ahu/${id}`, null).pipe(
      map(() => true),
      tap(() => {
        applyTransaction(() => {
          this.ahuStore.update(id, { Results: null })
          this.updateFormState({ isValid: true, isModified: false })
        })
        this.eventsService.ahuChanged(id)
        if (!omitSuccessMessage) {
          this.notificationService.success('UI.Success.AhuSaved')
        }
      }),
      catchError(error => {
        this.notificationService.error('UI.Error.AhuSaved')
        return throwError(error)
      }),
    )
  }

  export(id: string): void {
    this.sharedAhuService.downloadXMLExport(id)
  }

  delete(id: string): Observable<boolean> {
    return this.sharedAhuService.deleteAhu(id).pipe(
      tap({
        next: () => {
          this.eventsService.ahuChanged(id)
        },
      }),
    )
  }

  setActive(id: string | null): void {
    this.ahuStore.setActive(id)
  }

  getDxf = (id: string): void => {
    this.http
      .get(`./Api/ahu/${id}/Dxf`, { observe: 'response', responseType: 'blob' })
      .pipe(
        tap({
          next: response => {
            const filename = response.headers
              .get('content-disposition')
              .split(';')[1]
              .split('filename')[1]
              .split('=')[1]
              // eslint-disable-next-line quotes
              .split("''")[1]
              .trim()
            saveAs(response.body, filename)
          },
        }),
        untilDestroyed(this),
      )
      .subscribe()
  }

  private internalGetAnonymousAhu(countryId: string, unitCode?: string): Observable<Ahu> {
    // Select appropriate endpoint depending on if we have a unit code or not
    const requestUrl = unitCode ? `./Api/v2/ahu/country/${countryId}/unit/${unitCode}` : `./Api/ahu/country/${countryId}`

    return this.http.get<QuickCalcAhuDto>(requestUrl).pipe(
      tap(ahu => {
        this.translateService.use(ahu.Language)
      }),
      map(dto => {
        return ConvertAhuDtoToDomainModel(dto, null)
      }),
      tap(ahu => {
        applyTransaction(() => {
          this.ahuStore.upsert(ahu.Id, ahu)
          this.ahuStore.setHasCache(true)
        })
      }),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.error('UI.Error.GetAhu', { errorMessage: `Error: ${error?.error?.Message ?? error?.statusText}` })
        if (error?.status === 404) {
          this.router.navigateByUrl('/projects')
        }
        return throwError(() => error)
      }),
    )
  }

  private internalGetAhu(id: string, projectId: string): Observable<Ahu> {
    return this.http.get<AhuDto>(`./Api/ahu/${id}`).pipe(
      map(dto => {
        return ConvertAhuDtoToDomainModel(dto, projectId)
      }),
      tap(ahu => {
        applyTransaction(() => {
          this.ahuStore.upsert(id, ahu)
          this.ahuStore.setHasCache(true)
        })
      }),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.error('UI.Error.GetAhu', { errorMessage: `Error: ${error?.error?.Message ?? error?.statusText}` })
        if (error?.status === 404) {
          this.router.navigateByUrl('/projects')
        }
        return throwError(() => error)
      }),
    )
  }
}
